import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity, Search, RefreshCw, ChevronRight,
  ChevronDown, User, Clock, Monitor, Shield,
  HardDrive, Router, Layers, LogIn, AlertCircle,
  KeyRound, Cpu,
} from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { auditApi } from '../../services/adApi'
import { useAuth } from '../../contexts/AuthContext'
import { formatDateTime } from '../../utils/format'

const MODULE_CONFIG = {
  AD:      { labelKey: 'audit.modules.ad',      cls: 'bg-blue-50 text-blue-700',    Icon: Layers  },
  VPN:     { labelKey: 'audit.modules.vpn',     cls: 'bg-purple-50 text-purple-700',Icon: Shield  },
  Hosts:   { labelKey: 'audit.modules.hosts',   cls: 'bg-green-50 text-green-700',  Icon: Monitor },
  Routers: { labelKey: 'audit.modules.routers', cls: 'bg-orange-50 text-orange-700',Icon: Router  },
  Auth:    { labelKey: 'audit.modules.auth',    cls: 'bg-yellow-50 text-yellow-700',Icon: LogIn   },
  System:  { labelKey: 'audit.modules.system',  cls: 'bg-gray-100 text-gray-600',   Icon: Activity},
  Devices: { labelKey: 'audit.modules.devices', cls: 'bg-pink-50 text-pink-700',    Icon: HardDrive },
  Pki:     { labelKey: 'audit.modules.pki',     cls: 'bg-cyan-50 text-cyan-700',    Icon: KeyRound },
  Tpm:     { labelKey: 'audit.modules.tpm',     cls: 'bg-amber-50 text-amber-700',  Icon: Cpu },
}

const ACTION_CONFIG = {
  Create:  { labelKey: 'audit.actions.create',  cls: 'text-green-700 bg-green-50' },
  Update:  { labelKey: 'audit.actions.update',  cls: 'text-blue-700 bg-blue-50'   },
  Delete:  { labelKey: 'audit.actions.delete',  cls: 'text-red-700 bg-red-50'     },
  Login:   { labelKey: 'audit.actions.login',   cls: 'text-yellow-700 bg-yellow-50'},
  Logout:  { labelKey: 'audit.actions.logout',  cls: 'text-gray-600 bg-gray-100'  },
  Access:  { labelKey: 'audit.actions.access',  cls: 'text-blue-600 bg-blue-50'   },
  Execute: { labelKey: 'audit.actions.execute', cls: 'text-purple-700 bg-purple-50'},
  Sync:    { labelKey: 'audit.actions.sync',    cls: 'text-teal-700 bg-teal-50'  },
  Adopt:   { labelKey: 'audit.actions.adopt',   cls: 'text-indigo-700 bg-indigo-50'},
  Move:    { labelKey: 'audit.actions.move',    cls: 'text-orange-700 bg-orange-50'},
  Error:   { labelKey: 'audit.actions.error',   cls: 'text-red-700 bg-red-100' },
}

// Presets de aba: cada um pré-preenche os filtros de Module+Action.
// "Erros Certificados" e "Erros TPM" são as views diretas que o usuário pediu.
const TABS = [
  { id: 'all',     labelKey: 'audit.tabs.all',     filters: {} },
  { id: 'errPki', labelKey: 'audit.tabs.errPki', filters: { module: 'Pki', action: 'Error' } },
  { id: 'errTpm', labelKey: 'audit.tabs.errTpm', filters: { module: 'Tpm', action: 'Error' } },
]

function LogRow({ log }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const mod = MODULE_CONFIG[log.module] ?? MODULE_CONFIG.System
  const act = ACTION_CONFIG[log.action] ?? { labelKey: null, cls: 'text-gray-600 bg-gray-100' }
  const ModIcon = mod.Icon
  const actLabel = act.labelKey ? t(act.labelKey) : log.action
  const modLabel = t(mod.labelKey)

  return (
    <div className="border-b border-gray-100 last:border-0">
      <div
        className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <div className={clsx('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', mod.cls)}>
          <ModIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={clsx('text-xs font-medium px-1.5 py-0.5 rounded', act.cls)}>{actLabel}</span>
            <span className="text-sm text-gray-800 truncate">{log.description}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
            {(log.userName || log.userId) && (
              <span className="flex items-center gap-1 text-gray-600" title={log.userId || ''}>
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="font-medium">{log.userName || t('audit.userFallback', { id: String(log.userId).slice(0, 8) + '…' })}</span>
              </span>
            )}
            {log.ipAddress && <span>{log.ipAddress}</span>}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDateTime(log.timestamp)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={clsx('text-xs px-2 py-0.5 rounded-full', mod.cls)}>{modLabel}</span>
          {log.changes?.length > 0 && (
            <span className="text-xs text-gray-400">{t('audit.changesCount', { count: log.changes.length })}</span>
          )}
          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-3 text-xs mb-3">
            {log.entityType && (
              <div><span className="text-gray-500">{t('audit.entityType')}</span> <span className="font-medium text-gray-700">{log.entityType}</span></div>
            )}
            {log.entityId && (
              <div><span className="text-gray-500">{t('audit.entityId')}</span> <span className="font-mono text-gray-700 truncate">{log.entityId}</span></div>
            )}
            {log.entityName && (
              <div><span className="text-gray-500">{t('audit.entityName')}</span> <span className="font-medium text-gray-700">{log.entityName}</span></div>
            )}
          </div>
          {log.changes?.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-600 mb-1">{t('audit.changedFields')}</div>
              {log.changes.map((c, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 text-xs bg-white rounded-lg p-2 border border-gray-100">
                  <span className="font-medium text-gray-700">{c.fieldName}</span>
                  <span className="text-red-600 truncate" title={c.oldValue}>{c.oldValue ?? '—'}</span>
                  <span className="text-green-600 truncate" title={c.newValue}>{c.newValue ?? '—'}</span>
                </div>
              ))}
              <div className="grid grid-cols-3 gap-2 text-xs text-gray-400 px-2">
                <span />
                <span>{t('audit.before')}</span>
                <span>{t('audit.after')}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AuditPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const role = (user?.role || user?.Role || '').toLowerCase()
  const isGlobal = Boolean(user?.isGlobalUser || user?.IsGlobalUser)
  const isAdmin = isGlobal || role === 'admin' || role === 'owner' || role === 'global'

  const [activeTab, setActiveTab] = useState('all')
  const [filters, setFilters] = useState({
    module: '', action: '', userId: '', page: 1, pageSize: 50,
    from: '', to: '',
  })
  const [appliedFilters, setAppliedFilters] = useState({ page: 1, pageSize: 50 })

  // Busca por errorCode (UUID): GET /api/audit/error/{guid}. Independente da
  // listagem normal — quando o usuário cola um Guid, mostra o card único.
  const [errorCode, setErrorCode] = useState('')
  const [errorCodeApplied, setErrorCodeApplied] = useState('')
  const errorLogQuery = useQuery({
    queryKey: ['audit-error', errorCodeApplied],
    queryFn: () => auditApi.getErrorLog(errorCodeApplied),
    enabled: Boolean(errorCodeApplied),
    retry: false,
  })

  const handleTab = (tab) => {
    setActiveTab(tab.id)
    const next = { ...filters, ...tab.filters, module: tab.filters.module ?? '', action: tab.filters.action ?? '', page: 1 }
    setFilters(next)
    setAppliedFilters({ ...next, pageSize: appliedFilters.pageSize })
  }

  const handleErrorCodeSearch = (e) => {
    e.preventDefault()
    setErrorCodeApplied(errorCode.trim())
  }

  const { data: auditUsers } = useQuery({
    queryKey: ['audit-users'],
    queryFn: () => auditApi.getAuditUsers(),
    enabled: isAdmin,
  })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', appliedFilters],
    queryFn: () => auditApi.getLogs({
      ...appliedFilters,
      from: appliedFilters.from || undefined,
      to: appliedFilters.to || undefined,
      module: appliedFilters.module || undefined,
      action: appliedFilters.action || undefined,
      userId: appliedFilters.userId || undefined,
    }),
  })

  const handleSearch = (e) => {
    e.preventDefault()
    setAppliedFilters({ ...filters, page: 1 })
  }

  const logs = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('audit.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {isAdmin ? t('audit.subtitleAdmin') : t('audit.subtitleUser')}
          </p>
        </div>
        <button onClick={() => refetch()} className="btn btn-secondary text-sm flex items-center gap-1">
          <RefreshCw className="w-4 h-4" />
          {t('audit.refresh')}
        </button>
      </div>

      {/* Tabs de presets */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTab(tab)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Busca por código de erro (UUID) */}
      <div className="card p-4">
        <form onSubmit={handleErrorCodeSearch} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.errorCodeSearch')}
            </label>
            <input
              type="text"
              value={errorCode}
              onChange={(e) => setErrorCode(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="input text-sm font-mono"
            />
          </div>
          <button type="submit" className="btn btn-primary text-sm flex items-center gap-1">
            <Search className="w-4 h-4" />
            {t('audit.errorCodeFind')}
          </button>
          {errorCodeApplied && (
            <button
              type="button"
              onClick={() => { setErrorCode(''); setErrorCodeApplied('') }}
              className="btn btn-secondary text-sm"
            >
              {t('audit.errorCodeClear')}
            </button>
          )}
        </form>
        {errorCodeApplied && (
          <div className="mt-3">
            {errorLogQuery.isLoading ? (
              <div className="text-sm text-gray-400">{t('audit.loading')}</div>
            ) : errorLogQuery.error ? (
              <div className="text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {t('audit.errorCodeNotFound')}
              </div>
            ) : errorLogQuery.data ? (
              <LogRow log={errorLogQuery.data} />
            ) : null}
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="card p-4">
        <form onSubmit={handleSearch} className="flex items-end gap-3 flex-wrap">
          {isAdmin && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('audit.user')}</label>
              <select value={filters.userId} onChange={(e) => setFilters({ ...filters, userId: e.target.value })} className="input text-sm">
                <option value="">{t('audit.status.all')}</option>
                {(auditUsers ?? []).map((u) => (
                  <option key={u.userId} value={u.userId}>{u.userName || u.userId}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('audit.module')}</label>
            <select value={filters.module} onChange={(e) => setFilters({ ...filters, module: e.target.value })} className="input text-sm">
              <option value="">{t('audit.status.all')}</option>
              {Object.entries(MODULE_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{t(v.labelKey)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('audit.action')}</label>
            <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="input text-sm">
              <option value="">{t('audit.actions.all')}</option>
              {Object.entries(ACTION_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{t(v.labelKey)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('audit.filters.from')}</label>
            <input type="datetime-local" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="input text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('audit.filters.to')}</label>
            <input type="datetime-local" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="input text-sm" />
          </div>
          <button type="submit" className="btn btn-primary text-sm flex items-center gap-1">
            <Search className="w-4 h-4" />
            {t('audit.filter')}
          </button>
        </form>
      </div>

      {/* Resultados */}
      <div className="card">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-gray-500" />
            <h3 className="font-semibold text-gray-900">{t('audit.records')}</h3>
            {total > 0 && <span className="text-sm text-gray-500">{t('audit.totalCount', { total })}</span>}
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('audit.loading')}</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600 flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {t('audit.loadError')}
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">{t('audit.noRecords')}</div>
        ) : (
          <div>
            {logs.map((log) => <LogRow key={log.id} log={log} />)}
          </div>
        )}

        {/* Paginação */}
        {total > appliedFilters.pageSize && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {t('audit.page', { page: appliedFilters.page, total: Math.ceil(total / appliedFilters.pageSize) })}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={appliedFilters.page <= 1}
                onClick={() => setAppliedFilters((f) => ({ ...f, page: f.page - 1 }))}
                className="btn btn-secondary text-sm"
              >
                {t('audit.previous')}
              </button>
              <button
                disabled={appliedFilters.page >= Math.ceil(total / appliedFilters.pageSize)}
                onClick={() => setAppliedFilters((f) => ({ ...f, page: f.page + 1 }))}
                className="btn btn-secondary text-sm"
              >
                {t('audit.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
